/**
 * Merge two or more lists, one item per line.
 *
 * - **zip** — line i of every list joined with a separator: first names and last names
 *   become full names, terms and definitions become "term: definition".
 * - **concatenate** — every item of the first list, then the second, and so on.
 * - **interleave** — the first item of each list, then the second of each…
 * - **combinations** — every way of taking one item from each list (the cartesian
 *   product), joined with the separator. Its size is the product of the list lengths,
 *   so it is capped.
 *
 * Lists with no items are ignored, so an empty box never turns every line into a
 * dangling separator.
 */

export type MergeMode = "zip" | "concatenate" | "interleave" | "combinations";

export const MERGE_MODES: readonly MergeMode[] = [
  "zip",
  "concatenate",
  "interleave",
  "combinations",
];

/**
 * What zip and interleave do when the lists differ in length: stop at the shortest
 * list, fill the missing items with `fill`, or leave them out.
 */
export type UnevenMode = "pad" | "skip" | "shortest";

export const UNEVEN_MODES: readonly UnevenMode[] = ["pad", "skip", "shortest"];

export interface MergeOptions {
  mode: MergeMode;
  /** Joins the items of one output line in zip and combinations modes. */
  separator: string;
  uneven: UnevenMode;
  /** Stands in for a missing item when `uneven` is "pad". */
  fill: string;
  /** Strip spaces and tabs from the ends of each item. */
  trim: boolean;
  /** Drop blank lines from the input lists. */
  skipEmpty: boolean;
  /** Drop repeated lines from the result, keeping the first. */
  dedupe: boolean;
}

export const DEFAULT_MERGE_OPTIONS: MergeOptions = {
  mode: "zip",
  separator: " ",
  uneven: "pad",
  fill: "",
  trim: true,
  skipEmpty: true,
  dedupe: false,
};

/** The most lines combinations mode will generate. */
export const MAX_COMBINATIONS = 10_000;

export interface MergeResult {
  lines: string[];
  /** Items in each input list, after trimming and skipping. */
  counts: number[];
  /** Lines before the cap and before duplicates were removed. */
  total: number;
  /** Combinations were cut at `MAX_COMBINATIONS`. */
  truncated: boolean;
  duplicatesRemoved: number;
}

const LINE_BREAK = /\r\n|\r|\n/;

/** One list's items: one per line, trimmed and filtered as the options say. */
export function parseList(
  text: string,
  options: Pick<MergeOptions, "trim" | "skipEmpty">
): string[] {
  if (text.length === 0) {
    return [];
  }
  let items = text.split(LINE_BREAK);
  if (options.trim) {
    items = items.map((item) => item.trim());
  }
  if (options.skipEmpty) {
    items = items.filter((item) => item.length > 0);
  }
  return items;
}

/** Item `index` of every list, with missing items filled or left out per `uneven`. */
function rowAt(
  lists: readonly string[][],
  index: number,
  options: MergeOptions
): string[] {
  const row: string[] = [];
  for (const list of lists) {
    if (index < list.length) {
      row.push(list[index]);
    } else if (options.uneven === "pad") {
      row.push(options.fill);
    }
  }
  return row;
}

function rowCount(lists: readonly string[][], uneven: UnevenMode): number {
  const lengths = lists.map((list) => list.length);
  return uneven === "shortest" ? Math.min(...lengths) : Math.max(...lengths);
}

function zip(lists: readonly string[][], options: MergeOptions): string[] {
  const lines: string[] = [];
  const rows = rowCount(lists, options.uneven);
  for (let index = 0; index < rows; index++) {
    lines.push(rowAt(lists, index, options).join(options.separator));
  }
  return lines;
}

function interleave(
  lists: readonly string[][],
  options: MergeOptions
): string[] {
  const lines: string[] = [];
  const rows = rowCount(lists, options.uneven);
  for (let index = 0; index < rows; index++) {
    lines.push(...rowAt(lists, index, options));
  }
  return lines;
}

/** The size of the cartesian product, without building it. */
export function combinationCount(counts: readonly number[]): number {
  return counts.reduce((product, count) => product * count, 1);
}

function combinations(
  lists: readonly string[][],
  separator: string,
  limit: number
): string[] {
  const lines: string[] = [];
  const positions = lists.map(() => 0);
  while (lines.length < limit) {
    lines.push(
      positions.map((position, list) => lists[list][position]).join(separator)
    );
    // Advance like an odometer: the last list turns fastest.
    let list = lists.length - 1;
    while (list >= 0) {
      positions[list]++;
      if (positions[list] < lists[list].length) {
        break;
      }
      positions[list] = 0;
      list--;
    }
    if (list < 0) {
      break;
    }
  }
  return lines;
}

function withoutDuplicates(lines: readonly string[]): string[] {
  return [...new Set(lines)];
}

function mergeLines(
  lists: readonly string[][],
  options: MergeOptions
): { lines: string[]; total: number } {
  if (options.mode === "combinations") {
    return {
      lines: combinations(lists, options.separator, MAX_COMBINATIONS),
      total: combinationCount(lists.map((list) => list.length)),
    };
  }
  let lines: string[];
  if (options.mode === "zip") {
    lines = zip(lists, options);
  } else if (options.mode === "interleave") {
    lines = interleave(lists, options);
  } else {
    lines = lists.flat();
  }
  return { lines, total: lines.length };
}

export function mergeLists(
  texts: readonly string[],
  options: MergeOptions
): MergeResult {
  const parsed = texts.map((text) => parseList(text, options));
  const lists = parsed.filter((list) => list.length > 0);
  const { lines, total } =
    lists.length > 0 ? mergeLines(lists, options) : { lines: [], total: 0 };
  const kept = options.dedupe ? withoutDuplicates(lines) : lines;
  return {
    lines: kept,
    counts: parsed.map((list) => list.length),
    total,
    truncated: total > lines.length,
    duplicatesRemoved: lines.length - kept.length,
  };
}
